import React, { useRef, useState } from 'react'
import * as Dialog from '@radix-ui/react-dialog'
import {
  FaTimes as X,
  FaCamera as Camera,
  FaMagic as Magic,
} from 'react-icons/fa'
import { useLayoutStore } from '@/stores/layoutStore'
import { useUIStore } from '@/stores/uiStore'
import { CATEGORY_COLORS, CATEGORY_LABELS } from '@/constants/categories'
import {
  useIdentifyDevice,
  type IdentifyResult,
  type ResolvedMatch,
  type ResolvedGuess,
} from '@/hooks/useIdentifyDevice'
import { presetToDevice, gridPosition } from '@/lib/presetToDevice'
import { EditDeviceDialog } from './EditDeviceDialog'
import type { Device } from '@/types/devices'

interface PhotoAddDeviceDialogProps {
  isOpen: boolean
  onClose: () => void
}

export const PhotoAddDeviceDialog: React.FC<PhotoAddDeviceDialogProps> = ({
  isOpen,
  onClose,
}) => {
  const [file, setFile] = useState<File | null>(null)
  const [previewUrl, setPreviewUrl] = useState<string | null>(null)
  const [result, setResult] = useState<IdentifyResult | null>(null)
  const [guessEditorOpen, setGuessEditorOpen] = useState(false)
  const fileInputRef = useRef<HTMLInputElement>(null)
  const identify = useIdentifyDevice()

  const addDevice = useLayoutStore(s => s.addDevice)
  const devices = useLayoutStore(s => s.devices)
  const focusedZoneId = useUIStore(s => s.focusedZoneId)

  const resetState = () => {
    setFile(null)
    setPreviewUrl(prev => {
      if (prev) URL.revokeObjectURL(prev)
      return null
    })
    setResult(null)
    setGuessEditorOpen(false)
    identify.reset()
  }

  const handleClose = () => {
    resetState()
    onClose()
  }

  const handleFileSelected = (selected: File | null) => {
    if (previewUrl) URL.revokeObjectURL(previewUrl)
    if (!selected) {
      setFile(null)
      setPreviewUrl(null)
      setResult(null)
      return
    }
    setFile(selected)
    setPreviewUrl(URL.createObjectURL(selected))
    setResult(null)
  }

  const handleIdentify = async () => {
    if (!file) return
    try {
      const identified = await identify.mutateAsync(file)
      setResult(identified)
    } catch (err) {
      console.error('Identify failed', err)
      setResult({ matches: [], guess: null })
    }
  }

  const handleAcceptMatch = (match: ResolvedMatch) => {
    const device = presetToDevice(match.preset, {
      position: gridPosition(devices.length),
      zoneId: focusedZoneId ?? undefined,
    })
    addDevice(device)
    handleClose()
  }

  const handleUseGuess = () => {
    if (!result?.guess) return
    // Opens the EditDeviceDialog pre-filled with Claude's guess so
    // the user can confirm/tweak before the device lands on the
    // canvas. The guess isn't added silently — it's always a
    // starting point the user commits.
    setGuessEditorOpen(true)
  }

  const handleCreateFromGuess = (device: Device) => {
    addDevice({
      ...device,
      position: gridPosition(devices.length),
      zoneId: focusedZoneId ?? undefined,
    })
    setGuessEditorOpen(false)
    handleClose()
  }

  const matches = result?.matches ?? null
  const guess: ResolvedGuess | null = result?.guess ?? null

  const error = identify.error instanceof Error ? identify.error.message : null

  return (
    <Dialog.Root
      open={isOpen}
      onOpenChange={open => {
        if (!open) handleClose()
      }}
    >
      <Dialog.Portal>
        <Dialog.Overlay className="fixed inset-0 bg-black/50 backdrop-blur-sm z-40" />
        <Dialog.Content className="fixed left-1/2 top-1/2 -translate-x-1/2 -translate-y-1/2 w-full max-w-md max-h-[85vh] bg-surface-raised rounded-xl shadow-high z-50 flex flex-col">
          <div className="flex items-center justify-between px-6 py-4 border-b border-brd-subtle">
            <div>
              <Dialog.Title className="text-lg font-semibold text-content-primary flex items-center gap-2">
                <Camera className="w-4 h-4 text-accent" />
                Add device from photo
              </Dialog.Title>
              <Dialog.Description className="text-xs text-content-tertiary mt-0.5">
                Claude will match your photo against the built-in catalog.
              </Dialog.Description>
            </div>
            <Dialog.Close asChild>
              <button className="text-content-secondary hover:text-content-primary transition-colors duration-150">
                <X size={20} />
              </button>
            </Dialog.Close>
          </div>

          <div className="flex-1 overflow-y-auto px-6 py-4 space-y-4">
            <input
              ref={fileInputRef}
              type="file"
              accept="image/jpeg,image/png,image/webp,image/gif"
              className="hidden"
              onChange={e => handleFileSelected(e.target.files?.[0] ?? null)}
            />

            {!previewUrl && (
              <button
                type="button"
                onClick={() => fileInputRef.current?.click()}
                className="w-full border-2 border-dashed border-brd-subtle rounded-lg py-10 flex flex-col items-center gap-2 text-content-tertiary hover:border-accent hover:text-accent transition-colors duration-150"
              >
                <Camera size={28} />
                <span className="text-sm font-medium">
                  Choose a photo to identify
                </span>
                <span className="text-xs">JPG, PNG, WebP or GIF • max 5MB</span>
              </button>
            )}

            {previewUrl && (
              <div className="space-y-2">
                <img
                  src={previewUrl}
                  alt="Selected device"
                  className="w-full max-h-64 object-contain rounded-lg border border-brd-subtle bg-surface-sunken"
                />
                <div className="flex gap-2">
                  <button
                    type="button"
                    onClick={() => fileInputRef.current?.click()}
                    className="flex-1 px-3 py-2 rounded-lg bg-surface-sunken text-content-primary text-sm font-medium hover:bg-surface-overlay transition-colors duration-150"
                  >
                    Choose different photo
                  </button>
                  <button
                    type="button"
                    onClick={handleIdentify}
                    disabled={!file || identify.isPending}
                    className="flex-1 px-3 py-2 rounded-lg bg-gradient-to-r from-accent to-second text-[color:var(--accent-ink)] text-sm font-medium hover:brightness-110 disabled:opacity-50 disabled:cursor-not-allowed transition-[filter] duration-150"
                  >
                    {identify.isPending ? 'Identifying…' : '✨ Identify device'}
                  </button>
                </div>
              </div>
            )}

            {error && (
              <div className="text-xs text-destructive bg-destructive/5 border border-destructive/20 rounded-md px-3 py-2">
                {error}
              </div>
            )}

            {matches !== null && !identify.isPending && (
              <div className="space-y-4">
                {/* Catalog matches — only render when we have any. */}
                {matches.length > 0 && (
                  <div className="space-y-2">
                    <div className="text-xs font-semibold uppercase tracking-wider text-content-tertiary">
                      Catalog matches ({matches.length})
                    </div>
                    <ul className="space-y-2">
                      {matches.map((m, idx) => {
                        const category = m.preset.category
                        const confidencePct = Math.round(m.confidence * 100)
                        return (
                          <li
                            key={`${m.preset.manufacturer}-${m.preset.name}-${idx}`}
                            className="border border-brd-subtle rounded-lg p-3 flex items-start gap-3"
                          >
                            <span
                              className="mt-1 w-2.5 h-2.5 rounded-full flex-shrink-0"
                              style={{
                                backgroundColor: CATEGORY_COLORS[category],
                              }}
                              title={CATEGORY_LABELS[category]}
                            />
                            <div className="flex-1 min-w-0">
                              <div className="text-sm font-medium text-content-primary truncate">
                                {m.preset.manufacturer} {m.preset.name}
                              </div>
                              <div className="text-[11px] text-content-tertiary">
                                {m.preset.type} · confidence {confidencePct}%
                              </div>
                              {m.reason && (
                                <div className="text-[11px] italic text-content-tertiary mt-1">
                                  {m.reason}
                                </div>
                              )}
                            </div>
                            <button
                              type="button"
                              onClick={() => handleAcceptMatch(m)}
                              className="flex-shrink-0 px-3 py-1.5 rounded-lg bg-accent text-white text-xs font-semibold hover:bg-accent-hover transition-colors duration-150"
                            >
                              Add
                            </button>
                          </li>
                        )
                      })}
                    </ul>
                  </div>
                )}

                {/* Claude's best guess — always render if present. */}
                {guess && (
                  <div className="space-y-2">
                    <div className="text-xs font-semibold uppercase tracking-wider text-content-tertiary flex items-center gap-1.5">
                      <Magic className="w-3 h-3 text-second" />
                      Claude&apos;s best guess
                    </div>
                    <div className="border border-second bg-[color:var(--second-soft)] rounded-lg p-3">
                      <div className="flex items-start gap-3">
                        <span
                          className="mt-1 w-2.5 h-2.5 rounded-full flex-shrink-0"
                          style={{
                            backgroundColor:
                              CATEGORY_COLORS[guess.category] ?? '#999',
                          }}
                          title={CATEGORY_LABELS[guess.category]}
                        />
                        <div className="flex-1 min-w-0">
                          <div className="text-sm font-medium text-content-primary">
                            {guess.manufacturer} {guess.name}
                          </div>
                          <div className="text-[11px] text-content-tertiary">
                            {guess.type} · {guess.inputs.length} in ·{' '}
                            {guess.outputs.length} out · confidence{' '}
                            {Math.round(guess.confidence * 100)}%
                          </div>
                          {guess.reason && (
                            <div className="text-[11px] italic text-content-secondary mt-1">
                              {guess.reason}
                            </div>
                          )}
                        </div>
                        <button
                          type="button"
                          onClick={handleUseGuess}
                          className="flex-shrink-0 px-3 py-1.5 rounded-lg bg-gradient-to-r from-accent to-second text-[color:var(--accent-ink)] text-xs font-semibold hover:brightness-110 transition-[filter] duration-150"
                        >
                          Review & add
                        </button>
                      </div>
                      <p className="mt-2 text-[10px] text-content-tertiary">
                        This is Claude&apos;s independent read of the photo —
                        not from the built-in catalog. You&apos;ll be able to
                        tweak ports, category, and details before the device
                        lands on the canvas.
                      </p>
                    </div>
                  </div>
                )}

                {matches.length === 0 && !guess && (
                  <p className="text-xs text-content-tertiary">
                    Claude couldn&apos;t identify this photo. Try a clearer
                    shot, or use the manual Add Device flow instead.
                  </p>
                )}
              </div>
            )}
          </div>
        </Dialog.Content>
      </Dialog.Portal>
      {guess && (
        <EditDeviceDialog
          isOpen={guessEditorOpen}
          onClose={() => setGuessEditorOpen(false)}
          device={null}
          prefill={{
            name: guess.name,
            manufacturer: guess.manufacturer,
            category: guess.category,
            inputs: guess.inputs.map(p => ({
              name: p.name,
              signalType: p.signalType,
              connectorFormat: p.connectorFormat,
              bidirectional: p.bidirectional,
            })),
            outputs: guess.outputs.map(p => ({
              name: p.name,
              signalType: p.signalType,
              connectorFormat: p.connectorFormat,
              bidirectional: p.bidirectional,
            })),
          }}
          onCreate={handleCreateFromGuess}
        />
      )}
    </Dialog.Root>
  )
}
